<template>
  <div class="step-indicators">
    <!-- Circles and lines row -->
    <div class="d-flex align-center justify-center mb-3">
      <template
        v-for="(step, index) in steps"
        :key="step.number"
      >
        <div
          class="step-circle"
          :class="getStepCircleClasses(step.number)"
        >
          <div
            v-if="currentStep === step.number"
            class="step-number"
          >
            {{ step.displayNumber }}
          </div>
          <div
            v-else-if="currentStep > step.number"
            class="step-check"
          >
            <v-icon
              size="small"
              color="#ffffff"
            >
              mdi-check
            </v-icon>
          </div>
          <div
            v-else
            class="step-number inactive-step"
          >
            {{ step.displayNumber }}
          </div>
        </div>

        <!-- Line connector (except after last step) -->
        <div
          v-if="index < steps.length - 1"
          class="step-line bg-grey-lighten-1"
          :class="{ 'active-line': currentStep >= step.number + 1 }"
        />
      </template>
    </div>

    <!-- Labels row -->
    <div class="d-flex align-center">
      <template
        v-for="(step, index) in steps"
        :key="`label-${step.number}`"
      >
        <div class="step-label-container d-flex justify-center">
          <div class="step-label text-grey-darken-2">
            {{ $t(step.labelKey) }}
          </div>
        </div>
        <div
          v-if="index < steps.length - 1"
          class="step-label-spacer"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  currentStep: {
    type: Number,
    required: true,
    validator: (value) => value >= 1 && value <= 3,
  },
  steps: {
    type: Array,
    required: true,
    validator: (value) => {
      return value.every(
        (step) =>
          typeof step.number === 'number' &&
          typeof step.displayNumber === 'string' &&
          typeof step.labelKey === 'string'
      )
    },
  },
})

// Get CSS classes for step circle based on current step
const getStepCircleClasses = (stepNumber) => {
  return {
    active: props.currentStep === stepNumber,
    completed: props.currentStep > stepNumber,
    inactive: props.currentStep < stepNumber,
  }
}
</script>

<style scoped>
.step-indicators {
  position: relative;
  max-width: 400px;
  margin: 0 auto;
}

.step-label-container {
  width: 40px;
}

.step-label-spacer {
  flex: 1;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.step-circle.active {
  background-color: #ffffff !important;
  border: 2px solid rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-primary)) !important;
}

.step-circle.completed {
  background-color: rgb(var(--v-theme-primary)) !important;
  border: none !important;
  color: #ffffff !important;
}

.step-circle.inactive {
  background-color: #ffffff !important;
  border: 2px solid #bdbdbd !important;
  color: #bdbdbd !important;
}

.step-line {
  flex: 1;
  height: 2px;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  margin: 0;
  /* Background color handled by utility class */
}

.step-line.active-line {
  background-color: rgb(var(--v-theme-primary)) !important;
}

.step-number {
  font-weight: bold;
  font-size: 0.9rem;
}

.step-number.inactive-step {
  color: #bdbdbd;
}

.step-check {
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-label {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  /* Color handled by utility class */
}

@media (max-width: 600px) {
  .step-indicators {
    max-width: 320px;
  }

  .step-label {
    font-size: 0.7rem;
  }

  .step-label-container {
    width: 35px;
  }
}
</style>
